<template>
  <transition name="ns-slide">
    <div v-if="!isOnline" class="ns-banner">
      <span class="ns-text">{{ $t('pwa.networkOffline') }}</span>
      <span v-if="offlineDuration" class="ns-duration">({{ offlineDuration }})</span>

      <button
        v-if="showRetry"
        @click="manualRetry"
        class="ns-retry"
        :disabled="retrying"
      >
        {{ retrying ? $t('pwa.retrying') : $t('pwa.retry') }}
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'NetworkStatus',
  data () {
    return {
      isOnline: navigator.onLine,
      offlineTime: null,
      offlineDuration: '',
      showRetry: false,
      retrying: false,
      autoRetryTimer: null,
      durationTimer: null
    }
  },
  mounted () {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
  },
  beforeDestroy () {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    this.clearTimers()
  },
  methods: {
    handleOffline () {
      this.isOnline = false
      this.offlineTime = Date.now()
      this.startDurationTimer()
      setTimeout(() => {
        if (!this.isOnline) {
          this.showRetry = true
        }
      }, 30000)
      this.startAutoRetry()
    },

    handleOnline () {
      this.isOnline = true
      this.showRetry = false
      this.offlineDuration = ''
      this.clearTimers()
      if (this.$q && this.$q.notify) {
        this.$q.notify({
          type: 'positive',
          message: this.$t('pwa.backOnline'),
          position: 'top',
          timeout: 2000
        })
      }
    },

    startDurationTimer () {
      this.durationTimer = setInterval(() => {
        const seconds = Math.floor((Date.now() - this.offlineTime) / 1000)
        const minutes = Math.floor(seconds / 60)
        if (minutes > 0) {
          this.offlineDuration = `${minutes} ${this.$t('pwa.minutes')}`
        } else {
          this.offlineDuration = `${seconds} ${this.$t('pwa.seconds')}`
        }
      }, 1000)
    },

    startAutoRetry () {
      this.autoRetryTimer = setInterval(async () => {
        if (!this.isOnline) {
          await this.checkConnection(true)
        }
      }, 30000)
    },

    async manualRetry () {
      this.retrying = true
      await this.checkConnection(false)
      this.retrying = false
    },

    async checkConnection (silent = false) {
      try {
        const response = await fetch('/favicon2.ico', {
          method: 'HEAD',
          cache: 'no-cache'
        })
        if (response.ok && navigator.onLine) {
          this.handleOnline()
        }
      } catch (error) {
        if (!silent && this.$q && this.$q.notify) {
          this.$q.notify({
            type: 'warning',
            message: this.$t('pwa.stillOffline'),
            position: 'top',
            timeout: 2000
          })
        }
      }
    },

    clearTimers () {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
      if (this.autoRetryTimer) {
        clearInterval(this.autoRetryTimer)
        this.autoRetryTimer = null
      }
    }
  }
}
</script>

<style scoped>
.ns-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
}

.ns-text {
  flex: 1;
  text-align: center;
}

.ns-duration {
  opacity: 0.7;
  font-size: 12px;
  white-space: nowrap;
}

.ns-retry {
  padding: 4px 12px;
  background: #1a73e8;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.ns-retry:hover:not(:disabled) {
  opacity: 0.9;
}

.ns-retry:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ns-slide-enter-active,
.ns-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.ns-slide-enter,
.ns-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
